import { isPrincipalRef, principalRefFor, unverifiedSubject } from "../wire/addressing";
import type { JsonValue } from "../wire/json";
import { parseRelayMessage } from "../wire/parse";
import type {
  DropReport,
  FrameMessage,
  FrameReport,
  SessionState,
  SocketDrop,
  Welcome,
} from "../wire/protocol";
import {
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_PING,
  HEARTBEAT_PONG,
  WIRE_PROTOCOL_VERSION,
} from "../wire/protocol";

/**
 * The relay socket, held by the APP FRAME itself — #22's move out of `broker/`.
 *
 * ## Why this is not where it used to be
 *
 * The broker frame existed to hold ONE pairing credential across app origins:
 * its origin is constant, so its partitioned storage was the single place a
 * credential could be written once and found by the next app on another domain.
 * There is no stored credential any more — every frame proves the user for
 * itself — and each app frame already got its own broker document and its own
 * socket, so nothing was being shared through the shared origin at all.
 *
 * ⚠️ **The reason this matters is not tidiness.** While the broker constructed
 * the socket, the handshake `Origin` the relay saw was the BROKER's, identical
 * for every app — so the relay could not tell which app was talking, and the
 * Giraffe token carries no `app` claim to tell it. Dialling from the app frame
 * makes the browser stamp the connection with the app's real origin, which page
 * script cannot forge.
 *
 * Everything here runs in a browser frame and in a bare Node test: no
 * `document`, no `window`, no globals. That is what makes the reconnect and
 * account-switch paths testable at all — while they lived in `brokerFrame.ts`
 * their only coverage was a deployed relay and a real Chrome.
 */

/**
 * The minimum of `WebSocket` this module uses, declared rather than imported.
 *
 * `lib.dom`'s `WebSocket` is absent in a bare Node test, and a narrow
 * structural type lets a test supply a fake with no `as unknown as WebSocket`.
 */
export type SocketLike = {
  readonly readyState: number;
  addEventListener(type: string, fn: (event: SocketEvent) => void): void;
  send(data: string): void;
  close(): void;
};

/**
 * The fields of a `MessageEvent` and a `CloseEvent` this module reads, typed
 * `unknown` because one listener signature serves every event type.
 */
type SocketEvent = {
  readonly data?: unknown;
  readonly code?: unknown;
  readonly reason?: unknown;
};

/** The relay's refusal code — `refuse` in `relay/durableObject.ts`. */
const REFUSED = 4401;

/** `WebSocket.OPEN`, spelled out so this module needs no DOM global. */
const SOCKET_OPEN = 1;

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;

/**
 * Refusals in a row before the frame stops dialling.
 *
 * Every dial fetches a fresh token, so an expired token or an account switch
 * clears on the next one. A refusal that survives three fresh tokens will
 * survive the fourth, and each dial wakes the relay's account object to refuse
 * it again. `retry` is how a person starts it once more.
 */
const REFUSALS_BEFORE_STOPPING = 3;

/**
 * `docs/identity.md`'s tenant, as the dialling frame must compute it.
 *
 * ⚠️ It has to agree with the relay's `TENANT`, which is where the real value
 * lives. A disagreement is not silent: the ref would name an object the
 * verified identity does not hash to, and `admission.ts` refuses the socket
 * with a reason — the shape of failure this design prefers to a quiet
 * mis-route.
 */
const TENANT = "giraffe";

export type RelaySocketConfig = {
  readonly relayUrl: string;
  /**
   * The frame's own credential, RE-DERIVED PER DIAL rather than stored.
   *
   * A function and not a string, deliberately. The token lives about an hour
   * and a socket can re-dial long after the page booted, so a stored string
   * would present a stale token on reconnect and the relay would refuse it as
   * expired. ⚠️ That is not hypothetical tidiness: the app re-registers only
   * AFTER a successful `hello`, so a backoff loop replaying a stale token can
   * never recover on its own. This is what `docs/identity.md` means by "our design re-derives
   * rather than stores".
   */
  readonly getToken: () => Promise<string>;
  readonly openSocket: (url: string) => SocketLike;
  readonly schedule: (fn: () => void, delayMs: number) => void;
  /**
   * The heartbeat's timer, separate from `schedule` so the backoff and the beat
   * can be told apart. Absent means no heartbeat.
   */
  readonly scheduleHeartbeat?: (fn: () => void, delayMs: number) => void;
  /**
   * `drop` is null while connected, and says why whenever it is not. `stopped`
   * is true once the frame has given up dialling, and stays true until `retry`.
   */
  readonly onReady?: (
    connected: boolean,
    identity: VerifiedIdentity | null,
    drop: SocketDrop | null,
    stopped: boolean,
  ) => void;
  /**
   * Hand a drop to whoever sends it to the relay over HTTPS — see `DropReport`
   * for why the socket itself cannot carry it. Called on the 1st, 2nd, 4th,
   * 8th… drop in a row, so a frame stuck in a backoff loop reports for as long
   * as it is stuck without flooding the log.
   */
  readonly report?: (report: DropReport) => void;
  readonly onCall?: (call: RelayCallView) => void;
  readonly onSession?: (view: SessionPush) => void;
};

/**
 * Everything the relay's `welcome` said about who this socket belongs to — the
 * verified user, and the token's lists for the panel to match its own ids
 * against (#37). A derived fact, not a credential.
 */
export type VerifiedIdentity = Omit<Welcome, "t" | "v">;

/**
 * A call as this frame needs it — the relay's `frameId` DROPPED.
 *
 * There is exactly one frame on this socket now, so routing by frame id is a
 * question already answered by which socket the call arrived on. The broker
 * dropped it for the same reason and it is kept dropped deliberately: a field
 * the dispatcher could disagree with the socket about is a way to route a call
 * to the wrong place with nothing reporting it.
 */
export type RelayCallView = {
  readonly id: string;
  readonly tool: string;
  readonly args: Record<string, JsonValue>;
  readonly budgetMs: number;
};

/**
 * The session exactly as the relay pushed it.
 *
 * Deliberately NOT `brokerClient`'s `SessionView`, which adds `activeHere` —
 * the one field derived rather than reported. Keeping the pushed shape separate
 * is what stops a derived field being mistaken for the relay's own answer,
 * which is the shape of defect #10 was.
 */
export type SessionPush = {
  readonly state: SessionState;
  readonly activeProjectId: string | null;
  readonly controlProjectIds: readonly string[];
};

export type RelaySocket = {
  /** Report this frame. Its ORIGIN is not ours to state — see `FrameDescriptor`. */
  register(frame: FrameReport): Promise<void>;
  /** Detach for good: close the socket and stop reconnecting. */
  close(): void;
  /**
   * Dial again after the frame stopped on repeated refusals. Does nothing while
   * it is still dialling or after `close`.
   */
  retry(): Promise<void>;
  /**
   * The control app's tab came to the front (`docs/session-model.md`). Names
   * nothing: the relay activates the project this socket's token names, and
   * only if the token is the control app's.
   */
  front(): void;
  result(id: string, result: JsonValue): void;
};

export function createRelaySocket(config: RelaySocketConfig): RelaySocket {
  let token: string | null = null;
  let ref: string | null = null;
  let frame: FrameReport | null = null;
  let socket: SocketLike | null = null;
  let identity: VerifiedIdentity | null = null;
  let attempt = 0;
  // Dials in a row that closed without opening. Separate from `attempt`, which
  // paces the backoff; this is the count the panel reports.
  let unopened = 0;
  // Drops since the relay last WELCOMED this frame. Not reset by `open`: a
  // refusal opens every time, and a count reset there would report the first
  // refusal of an endless loop over and over.
  let consecutive = 0;
  // Refusals with no other kind of drop and no welcome between them.
  let refusals = 0;
  let stopped = false;
  let detached = false;

  const announce = (connected: boolean, drop: SocketDrop | null = null): void => {
    config.onReady?.(connected, identity, drop, stopped);
  };

  const toRelay = (message: FrameMessage): boolean => {
    if (socket === null || socket.readyState !== SOCKET_OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  };

  /**
   * Dial, IDEMPOTENTLY.
   *
   * The early return on an existing socket is load-bearing rather than tidy:
   * `connect` is reached from a first registration, from a deliberate re-dial
   * when the account changed, and from a backoff timer after a drop — and a
   * timer firing just after a re-dial would otherwise open a second socket to
   * the same object. Two sockets is not a visible failure; it is two copies of
   * every frame in one session's registry.
   */
  const connect = (): void => {
    if (detached) return;
    if (socket !== null) return;
    const currentRef = ref;
    const currentToken = token;
    // Nothing to dial with. Not an error and not a retry loop: the app has not
    // registered yet, and `register` is what starts this.
    if (currentRef === null || currentToken === null) return;

    const url = new URL(config.relayUrl);
    // ⚠️ The ref and NOTHING else. The token is a live credential and
    // Cloudflare logs request URLs on the Worker -> Durable Object hop as well
    // as the public one (#17, measured). It crosses in `hello`, over the
    // socket, which is not logged.
    url.searchParams.set("ref", currentRef);
    const next = config.openSocket(url.toString());
    socket = next;
    let opened = false;
    let answered = true;

    /**
     * One beat (#66). The relay answers the ping without waking (see
     * `HEARTBEAT_PING`), which is the only reason a timer is allowed here. No
     * pong since the last beat means the socket is dead without having said so,
     * and it is handed to `retry` HERE rather than by its `close` event: on a
     * half-open connection, which is the case this exists for, the browser may
     * not fire `close` until its own timeout. `retry` lets go of the socket
     * first, so the `close` that does come later is recognised as superseded.
     */
    const beat = (): void => {
      if (socket !== next) return;
      if (!answered) {
        retry({});
        next.close();
        return;
      }
      answered = false;
      next.send(HEARTBEAT_PING);
      config.scheduleHeartbeat?.(beat, HEARTBEAT_INTERVAL_MS);
    };

    next.addEventListener("open", () => {
      opened = true;
      config.scheduleHeartbeat?.(beat, HEARTBEAT_INTERVAL_MS);
      toRelay({ t: "hello", v: WIRE_PROTOCOL_VERSION, token: currentToken });
      // ⚠️ NOT `attempt`: that resets on `welcome`. A socket that opens has
      // only completed the handshake, and a refusal arrives after it — reset
      // here, a relay refusing every dial was re-dialled every 500ms forever.
      unopened = 0;
      // A reconnect must re-report the frame: the relay holds NO state for a
      // socket that has gone (`docs/session-model.md`, "reconnect does not silently restore").
      if (frame !== null) toRelay({ t: "frames", frames: [frame] });
      announce(true);
    });

    next.addEventListener("message", (event) => {
      if (event.data === HEARTBEAT_PONG) {
        answered = true;
        return;
      }
      const parsed = parseRelayMessage(String(event.data));
      if (!parsed.ok) return;
      const message = parsed.message;
      if (message.t === "welcome") {
        // Accepted, which `open` alone never meant: the backoff and the report
        // count both start again here, and only here.
        attempt = 0;
        consecutive = 0;
        refusals = 0;
        // Who the relay VERIFIED this socket to be. A derived fact, not a
        // credential: there is nothing here to persist and nothing to rejoin
        // with, which is the whole difference from the install id it replaced.
        identity = {
          userId: message.userId,
          userName: message.userName,
          workspaces: message.workspaces,
          projects: message.projects,
        };
        announce(true);
        return;
      }
      if (message.t === "call") {
        config.onCall?.({
          id: message.id,
          tool: message.tool,
          args: message.args,
          budgetMs: message.budgetMs,
        });
        return;
      }
      if (message.t === "session") {
        // Forwarded verbatim and NOT remembered. The relay pushes on every
        // change, so a cached copy here could only ever be a second, staler
        // answer to a question that already has an authoritative one.
        config.onSession?.({
          state: message.state,
          activeProjectId: message.activeProjectId,
          controlProjectIds: message.controlProjectIds,
        });
        return;
      }
      // `abandon`: the relay stopped waiting. Nothing to do here — this frame's
      // answer will arrive at a correlation table that has forgotten it, which
      // the relay treats as the expected late answer that it is.
    });

    const classify = (event: SocketEvent): SocketDrop => {
      const code = typeof event.code === "number" ? event.code : null;
      if (code === REFUSED) {
        return { kind: "refused", reason: typeof event.reason === "string" ? event.reason : "" };
      }
      if (!opened) {
        unopened += 1;
        return { kind: "unreachable", attempts: unopened };
      }
      return { kind: "dropped", code };
    };

    const retry = (event: SocketEvent): void => {
      // A socket that has already been SUPERSEDED closes too, and it must not
      // start a backoff: the account switch replaced it on purpose, and
      // treating a deliberate re-dial as a failure is how the user's own
      // account switch turns into a delay that looks like an outage — and
      // races a second socket against the one that just replaced it.
      if (socket !== next) return;
      socket = null;
      // A dropped socket loses the identity it was told; holding it would leave
      // the panel naming a user nothing is currently serving.
      identity = null;
      const drop = classify(event);
      refusals = drop.kind === "refused" ? refusals + 1 : 0;
      if (!detached && refusals >= REFUSALS_BEFORE_STOPPING) stopped = true;
      announce(false, drop);
      // A deliberate detach is not an outage; see below.
      if (!detached && ref !== null) {
        consecutive += 1;
        // Powers of two: 1, 2, 4, 8… A bit test, because `consecutive` is a
        // positive integer and that is exactly the set with one bit set.
        if ((consecutive & (consecutive - 1)) === 0) {
          config.report?.({ t: "drop_report", v: WIRE_PROTOCOL_VERSION, ref, consecutive, drop });
        }
      }
      // A deliberate detach is not an outage. Backing off here would reconnect
      // a frame the page has finished with, and on a panel opened and closed
      // repeatedly that accumulates sockets.
      if (detached) return;
      if (stopped) return;
      attempt += 1;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);
      // `refresh`, never `connect`: the re-dial must obtain a NEW token. A
      // backoff that replays the one this socket died holding cannot recover.
      config.schedule(() => {
        void refresh();
      }, delay);
    };
    next.addEventListener("close", retry);
    next.addEventListener("error", () => {
      next.close();
    });
  };

  /**
   * Obtain a token, work out which account object it addresses, then dial — or
   * RE-dial if it addresses a different one.
   *
   * The re-dial is not defensive tidiness. A Giraffe user can switch accounts
   * without reloading the panel, and a socket left attached to the previous
   * account's object would keep serving the wrong person's session. Nothing
   * would refuse it, because it was admitted legitimately; it is the one
   * failure here that would otherwise be silent.
   *
   * ⚠️ The subject is read from the token WITHOUT verification, and that is
   * sound only because it is used for one thing: choosing an address. The
   * Durable Object verifies the token and refuses the socket if the verified
   * identity does not hash to the object it landed on. See
   * `wire/addressing.ts`'s `unverifiedSubject`.
   */
  const refresh = async (redial = false): Promise<void> => {
    const registered = await config.getToken();
    token = registered;

    const subject = unverifiedSubject(registered);
    // A token naming nobody has no address. Refused here rather than dialled
    // somewhere harmless, because "connected but attached to nothing" is the
    // state `docs/tool-surface.md`'s status vocabulary exists to make impossible.
    if (subject === null) return;

    const next = await principalRefFor(TENANT, subject);
    // Shape-checked before it names a Durable Object. `principalRefFor` returns
    // 32 hex characters by construction, so this is unreachable today — and it
    // costs nothing at the one place in the browser where an object name is
    // minted.
    if (!isPrincipalRef(next)) return;

    if (!redial && next === ref && socket !== null) {
      // Same account, socket already up: the token is refreshed for the NEXT
      // dial and this one is left alone. Re-dialling here would drop a live
      // session every time an app re-registered — which it does on every
      // reconnect.
      return;
    }

    const previous = socket;
    ref = next;
    // Cleared BEFORE the close, which is what makes `retry` recognise this
    // socket as superseded rather than dropped.
    socket = null;
    previous?.close();
    // Said out loud, though it is not a failure: the relay forgets everything
    // about the old socket, and whoever claims a project on the edge into
    // "connected" (the control app) needs that edge to happen again.
    if (previous !== null) announce(false);
    connect();
  };

  const register = async (descriptor: FrameReport): Promise<void> => {
    // A NEW PROJECT needs a new token (#67): the relay admits a frame only for
    // the project its token names, so a re-report under the old token would
    // leave this frame connected and unreachable. Re-dialling is the only way a
    // fresh token reaches the relay — `hello` is sent once per socket.
    const moved = frame !== null && frame.projectId !== descriptor.projectId;
    frame = descriptor;
    const before = socket;
    await refresh(moved);
    // Same account, same socket: `refresh` leaves it alone, so the new report
    // goes out here. The relay replaces a connection's frames wholesale, so a
    // project switch is one more `frames` message. A socket still connecting
    // needs nothing — its `open` sends whatever `frame` is by then.
    //
    // ⚠️ `frame`, not `descriptor`: two registers can overlap, and the one whose
    // token read finishes LAST must not put the older frame back.
    if (socket !== null && socket === before && frame !== null) {
      toRelay({ t: "frames", frames: [frame] });
    }
  };

  return {
    register,
    front: (): void => {
      toRelay({ t: "front" });
    },
    result: (id: string, result: JsonValue): void => {
      toRelay({ t: "result", id, result });
    },
    close: (): void => {
      detached = true;
      socket?.close();
    },
    retry: async (): Promise<void> => {
      if (detached || !stopped) return;
      stopped = false;
      refusals = 0;
      attempt = 0;
      announce(false);
      await refresh();
    },
  };
}
